import { formatItemsPerTransactionToBackend } from '@components/forms/utils/format-items-per-transaction-to-backend';
import { formatWeeklyValidityToBackend } from '@components/forms/utils/format-pass-validity-to-backend';
import { formatPriceToBackend } from '@components/forms/utils/format-price-to-backend';
import { formatSalesTimeToBackend } from '@components/forms/utils/format-sales-time-to-backend';
import { formatSalesStatusToBackend } from '@components/forms/utils/format-status-to-backend';
import { type CreatePassRequest } from '@components/passes/pass-create-modal/pass-create-modal.type';
import { type SupportedLanguages } from '@constants/constants.type';
import { ADMISSION_STATUS_HIDDEN, ADMISSION_TYPE_GUEST_LIST } from '@domain/admission';
import { type FormFieldPreset } from '@domain/form-field-presets';
import { type Pass } from '@domain/passes';
import { toBackend } from '@utils/map-date/to-backend';
import moment from 'moment-timezone';

import { type AdmissionFormValue } from '../admission-form.type';
import { valuesEqual } from '../validation';

import { mapHolderGroupToBackend } from './form-fields';
import {
  applyItalyRegulationGranularity,
  applySettingsGranularity,
  isNominativeDirty,
  resolveTicketsLeftThreshold,
  toAccessCode,
  toBackendTranslations,
  toChannelSettings,
  toCheckInTime,
  toItalyRegulation,
} from './to-backend';

export interface ToPassBackendContext {
  value: AdmissionFormValue;
  currency: string;
  mainLanguage: SupportedLanguages;
  companyId: string;
  timezone: string;
  pass?: Pass | null;
  isEventPass: boolean;
  eventStartingTime?: number;
  eventEndingTime?: number;
  defaultTicketsLeftThreshold: number;
  formFieldPresets: FormFieldPreset[];
  selectedOfferTriggerId: string | null;
  isSiaeApprovalAllowed: boolean;
  isDuplicate?: boolean;
}

const toPassValidity = (
  passValidity: AdmissionFormValue['basicInfo']['passValidity'],
  timezone: string
): CreatePassRequest['passValidity'] | undefined => {
  if (!passValidity) return undefined;
  return formatWeeklyValidityToBackend({
    fromValidity: passValidity.start,
    untilValidity: passValidity.end,
    timezone,
    weekDays: passValidity.days.map((day) => ({ id: day, name: day, checked: true })),
  });
};

type PassItalyRegulation = NonNullable<NonNullable<CreatePassRequest['settings']>['italyRegulation']>;

// Pass payloads omit `dirittoPrevendita` when there is no DDP (legacy parity), whereas the shared
// admission builder carries `number | null`.
const toPassItalyRegulation = (regulation: ReturnType<typeof toItalyRegulation>): PassItalyRegulation | undefined => {
  if (!regulation) return undefined;
  const { dirittoPrevendita, ...rest } = regulation;
  return { ...rest, ...(dirittoPrevendita !== null && { dirittoPrevendita }) };
};

// Legacy parity (getTicketFromForm): a selected trigger sets `offerReleasedBefore`; clearing one that
// existed sends `null`; otherwise the key is omitted.
const toOfferReleasedBefore = (
  selectedOfferTriggerId: string | null,
  pass: Pass | null | undefined
): string | null | undefined => {
  if (selectedOfferTriggerId) return selectedOfferTriggerId;
  if (pass?.offerReleasedBefore) return null;
  return undefined;
};

/**
 * Builds the full `CreatePassRequest` from the chassis form value. Passes diverge from admissions:
 * no add-ons, validity is `passValidity` (from/until/days), and the sales schedule applies to event
 * passes only (salesStatus is sent for every pass). The drawer owns the create/edit endpoint split
 * and the dirty-diff on edit.
 */
export const toCreatePassRequest = ({
  value,
  currency,
  mainLanguage,
  companyId,
  timezone,
  pass,
  isEventPass,
  eventStartingTime,
  eventEndingTime,
  defaultTicketsLeftThreshold,
  formFieldPresets,
  selectedOfferTriggerId,
  isSiaeApprovalAllowed,
  isDuplicate = false,
}: ToPassBackendContext): CreatePassRequest => {
  const { basicInfo, advanced, appearance, vatRate, formFields, distribution } = value;
  const isCreate = !pass || isDuplicate;

  const lowStock = advanced.lowStockThreshold;
  const ticketsLeftThreshold = resolveTicketsLeftThreshold(lowStock, defaultTicketsLeftThreshold, isCreate);
  const { channels, isForAllChannels, channelDefaultMaxQuantity } = toChannelSettings(distribution.channelLimit);
  const italyRegulation = toPassItalyRegulation(toItalyRegulation(basicInfo, value.regulation, isSiaeApprovalAllowed));

  const request: CreatePassRequest = {
    ...(isCreate && { companyId, onlinePaymentPercentage: 100 }),
    price: formatPriceToBackend({ price: basicInfo.price, currency, type: 'pass' }),
    quantity: basicInfo.quantity ? Number(basicInfo.quantity) : null,
    itemsPerTransaction: formatItemsPerTransactionToBackend({
      min: basicInfo.itemsPerTransaction.min,
      max: basicInfo.itemsPerTransaction.max,
      quantity: Number(basicInfo.quantity),
      type: ADMISSION_TYPE_GUEST_LIST,
    }),
    name: toBackendTranslations(basicInfo.title, mainLanguage, basicInfo.translationsEnabled),
    description: toBackendTranslations(basicInfo.description, mainLanguage, basicInfo.translationsEnabled),
    color: appearance.color ?? null,
    vatRate: vatRate.vatRate,
    accessCode: toAccessCode(distribution.accessCode),
    channels,
    settings: {
      isHidden: value.status === ADMISSION_STATUS_HIDDEN,
      distribution: distribution.mode,
      isForAllChannels,
      channelDefaultMaxQuantity,
      isNominative: formFields.isNominative,
      ...(italyRegulation && { italyRegulation }),
    },
    checkInTime: toCheckInTime(advanced.entranceTimeSlot, pass, timezone),
    bookingFormFields: mapHolderGroupToBackend(formFields.mainHolder, pass?.bookingFormFields, formFieldPresets),
    ticketHolderFormFields: formFields.isNominative
      ? mapHolderGroupToBackend(formFields.ticketHolder, pass?.ticketHolderFormFields, formFieldPresets)
      : [],
    ...(lowStock && { ticketsLeftThreshold }),
  };

  const passValidity = toPassValidity(basicInfo.passValidity, timezone);
  if (passValidity) request.passValidity = passValidity;

  request.salesStatus = formatSalesStatusToBackend({ offer: pass, status: value.status, prevStatus: pass?.status });

  if (isEventPass && basicInfo.salesWindow && eventStartingTime !== undefined && eventEndingTime !== undefined) {
    const { startDate, startTime, startType, endDate, endTime, endType } = basicInfo.salesWindow;
    request.salesTime = formatSalesTimeToBackend({
      salesStartDate: startDate,
      salesStartTime: startTime,
      salesStartType: startType,
      salesEndDate: endDate,
      salesEndTime: endTime,
      salesEndType: endType,
      eventStartingTime,
      eventEndingTime,
      timezone,
    });
  }

  const offerReleasedBefore = toOfferReleasedBefore(selectedOfferTriggerId, pass);
  if (offerReleasedBefore !== undefined) request.offerReleasedBefore = offerReleasedBefore;

  if (!isEventPass && !isDuplicate && offerReleasedBefore === null) {
    request.salesTime = { openingTime: toBackend(moment.tz(timezone)) };
  }

  if (isDuplicate && pass) request.clonedFromPassId = pass.id;

  return request;
};

/**
 * Trim `salesTime` to the sub-fields that actually changed. Mirrors admission's
 * `applySalesTimeGranularity` but without its `offerReleasedBefore` reset — the pass rebuilds
 * `offerReleasedBefore` from form state, so the generic diff already handles it.
 */
const applyPassSalesTimeGranularity = (payload: CreatePassRequest, baseline: CreatePassRequest): void => {
  if (!payload.salesTime) return;
  const granular: NonNullable<CreatePassRequest['salesTime']> = {};
  if (!valuesEqual(payload.salesTime.openingTime, baseline.salesTime?.openingTime))
    granular.openingTime = payload.salesTime.openingTime;
  if (!valuesEqual(payload.salesTime.closingTime, baseline.salesTime?.closingTime))
    granular.closingTime = payload.salesTime.closingTime;

  if (Object.keys(granular).length === 0) delete payload.salesTime;
  else payload.salesTime = granular;
};

/**
 * Trim `passValidity` to the sub-fields that actually changed — legacy PATCHed only the edited
 * validity field (start / end / weekdays), not all three, and the BE merges a partial object.
 */
const applyPassValidityGranularity = (payload: CreatePassRequest, baseline: CreatePassRequest): void => {
  if (!payload.passValidity) return;
  const granular: NonNullable<CreatePassRequest['passValidity']> = {};
  if (!valuesEqual(payload.passValidity.from, baseline.passValidity?.from)) granular.from = payload.passValidity.from;
  if (!valuesEqual(payload.passValidity.until, baseline.passValidity?.until))
    granular.until = payload.passValidity.until;
  if (!valuesEqual(payload.passValidity.passDayValidity, baseline.passValidity?.passDayValidity))
    granular.passDayValidity = payload.passValidity.passDayValidity;

  if (Object.keys(granular).length === 0) delete payload.passValidity;
  else payload.passValidity = granular;
};

export const diffPassRequest = (
  current: CreatePassRequest,
  baseline: CreatePassRequest,
  alwaysKeep: (keyof CreatePassRequest)[] = [],
  isStatusDirty = false
): CreatePassRequest => {
  const channelGroupDirty =
    !valuesEqual(current.channels, baseline.channels) ||
    !valuesEqual(current.settings?.isForAllChannels, baseline.settings?.isForAllChannels) ||
    !valuesEqual(current.settings?.channelDefaultMaxQuantity, baseline.settings?.channelDefaultMaxQuantity);

  const nominativeChanged = isNominativeDirty(current, baseline);
  const changed: CreatePassRequest = {};
  for (const key of Object.keys(current) as (keyof CreatePassRequest)[]) {
    const keep =
      key === 'channels'
        ? channelGroupDirty
        : (nominativeChanged && key === 'ticketHolderFormFields') ||
          alwaysKeep.includes(key) ||
          !valuesEqual(current[key], baseline[key]);
    if (keep) Object.assign(changed, { [key]: current[key] });
  }

  applySettingsGranularity(changed, current.settings, baseline.settings, isStatusDirty, channelGroupDirty);
  applyItalyRegulationGranularity(changed, current.settings, baseline.settings);
  applyPassSalesTimeGranularity(changed, baseline);
  applyPassValidityGranularity(changed, baseline);
  return changed;
};
